import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { ArrowRight, Upload, Shield, Globe, Zap, Database, Server, Cloud } from 'lucide-react';

const OverlayHostingServices: React.FC = () => {
  const [key, setKey] = useState(0);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 md:px-8 lg:px-10 max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          <div className="lg:w-1/2">
            <motion.h1
              className="text-4xl md:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-primary to-blue-500"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Overlay Hosting Services
            </motion.h1>
            <motion.p
              className="text-xl md:text-2xl font-medium mb-8 text-foreground/80"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Managed cloud environment ensuring reliable, scalable, and secure blockchain infrastructure.
            </motion.p>
            <motion.div
              className="mb-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <span className="text-sm text-muted-foreground px-3 py-1 bg-muted rounded-full">
                Backed by open source software.
              </span>
            </motion.div>
            {/* Combined CTA Buttons Container */}
            <motion.div
              className="flex flex-col md:flex-row flex-wrap items-center gap-4 mt-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Button size="lg" className="gap-2">
                Coming Soon
              </Button>
              <a
                href="https://github.com/bitcoin-sv/cars-node"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Learn more about Overlay Hosting Services with CARS"
                className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-11 px-8 gap-2"
                role="button"
              >
                View Documentation
              </a>
              {/* Contact CTA Button - Applying standard outline style */}
              <a
                href="/Contact"
                aria-label="Contact us to get started"
                // Applying classes for Button size="lg" variant="outline"
                className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-11 px-8 gap-2"
                role="button"
              >
                Contact Us
                <ArrowRight className="w-4 h-4" /> {/* Consistent icon size */}
              </a>
            </motion.div>
          </div>
          <motion.div
            className="lg:w-1/2"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <div className="flex items-center justify-center">
              <motion.div
                key={key}
                initial={{ scale: 0.8 }}
                animate={{
                  scale: [0.8, 1.1, 1],
                  rotate: [0, 5, 0]
                }}
                transition={{
                  duration: 2,
                  ease: "easeInOut"
                }}
                whileHover={{ cursor: "pointer" }}
                onClick={() => setKey(prev => prev + 1)}
                className="relative"
              >
                {/* Main Icon */}
                <Database className="h-48 w-48 text-primary" />

                {/* Decorative Elements */}
                <motion.div
                  key={`${key}-1`}
                  className="absolute -top-4 -right-4"
                  initial={{ scale: 1, opacity: 0.5 }}
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.5, 1, 0.5]
                  }}
                  transition={{
                    duration: 2,
                    ease: "easeInOut"
                  }}
                >
                  <Server className="h-16 w-16 text-blue-500/50" />
                </motion.div>

                <motion.div
                  key={`${key}-2`}
                  className="absolute -bottom-4 -left-4"
                  initial={{ scale: 1, opacity: 0.5 }}
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.5, 1, 0.5]
                  }}
                  transition={{
                    duration: 2,
                    ease: "easeInOut"
                  }}
                >
                  <Globe className="h-16 w-16 text-primary/50" />
                </motion.div>

                <motion.div
                  key={`${key}-3`}
                  className="absolute -bottom-4 -right-4"
                  initial={{ scale: 1, opacity: 0.5 }}
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.5, 1, 0.5]
                  }}
                  transition={{
                    duration: 2,
                    ease: "easeInOut"
                  }}
                >
                  <Cloud className="h-16 w-16 text-blue-500/50" />
                </motion.div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-muted/50">
        <div className="px-4 sm:px-6 md:px-8 lg:px-10 max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Key Features</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Everything you need to get up and running with Overlay Services
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Database className="h-8 w-8 text-primary" />,
                title: "Automated Kubernetes Provisioning",
                description: "CARS Node interacts with a Kubernetes cluster to schedule workloads, manage pods and services, and ensure high availability."
              },
              {
                icon: <Globe className="h-8 w-8 text-primary" />,
                title: "Dynamic Ingress and SSL",
                description: "Uses ingress-nginx, cert-manager, and Let's Encrypt to automatically provision custom domains and HTTPS certificates."
              },
              {
                icon: <Upload className="h-8 w-8 text-primary" />,
                title: "Identity and Project Management",
                description: "Integrates with the standard BSV identity system, ensuring only authorized admins can create or manage projects."
              },
              {
                icon: <Shield className="h-8 w-8 text-primary" />,
                title: "Billing and Resource Usage Tracking",
                description: "Integrates with Prometheus to gather CPU, memory, disk, and network usage over time, billing projects automatically."
              },
              {
                icon: <Zap className="h-8 w-8 text-primary" />,
                title: "Logging and Observability",
                description: "Centralized logs in MySQL, plus direct access to cluster-level logs (frontend/backend/mongo/mysql) via kubectl and API endpoints."
              },
              {
                icon: <Shield className="h-8 w-8 text-primary" />,
                title: "Extensible Setup",
                description: "Designed for both small-scale Docker Compose-based setups and large-scale, production-grade environments."
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                className="bg-card border rounded-xl p-8 shadow-sm hover:shadow-md transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="mb-4 bg-primary/10 w-14 h-14 rounded-lg flex items-center justify-center">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20">
        <div className="px-4 sm:px-6 md:px-8 lg:px-10 max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Use Cases</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              How businesses leverage Overlay Hosting Services for content management
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: "Digital Media Companies",
                description: "Media organizations use Overlay Hosting Services to distribute articles, videos, and podcasts with proof of publication and immutable timestamps.",
                image: "/images/donald-tong-media.jpg",
                placeholder: "https://placehold.co/600x400/4f46e5/ffffff?text=Media+Use+Case"
              },
              {
                title: "Enterprise Documentation",
                description: "Enterprises maintain critical documentation with versioning, access control, and guaranteed availability across global offices.",
                image: "/images/artem-podrez-enterprise.jpg",
                placeholder: "https://placehold.co/600x400/4f46e5/ffffff?text=Enterprise+Use+Case"
              },
              {
                title: "Educational Institutions",
                description: "Schools and universities distribute educational materials to students with trackable usage and secure access controls.",
                image: "/images/christina-morillo-education.jpg",
                placeholder: "https://placehold.co/600x400/4f46e5/ffffff?text=Education+Use+Case"
              },
              {
                title: "Content Creators",
                description: "Creators publish and monetize their content directly to audiences without intermediaries, maintaining ownership and control.",
                image: "/images/andrea-piacquadio-creator.jpg",
                placeholder: "https://placehold.co/600x400/4f46e5/ffffff?text=Creators+Use+Case"
              }
            ].map((useCase, index) => (
              <motion.div
                key={index}
                className="bg-card border rounded-xl overflow-hidden shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={useCase.image}
                    alt={useCase.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = useCase.placeholder;
                    }}
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-2">{useCase.title}</h3>
                  <p className="text-muted-foreground">{useCase.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary/5">
        <div className="px-4 sm:px-6 md:px-8 lg:px-10 max-w-7xl mx-auto">
          <div className="bg-card border rounded-2xl p-8 md:p-12 shadow-lg">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <div>
                <h2 className="text-3xl font-bold mb-4">Ready to transform your business?</h2>
                {/* <p className="text-muted-foreground mb-6 md:mb-0">
                  Coming soon!
                </p> */}
              </div>
              <div className="flex flex-wrap gap-4">
                {/* <Button size="lg" className="gap-2">
                  Get Started <ArrowRight className="h-4 w-4" />
                </Button> */}
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => window.location.href = '/contact'}
                >
                  Request Demo
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OverlayHostingServices; 